import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormulaireJourAdmissible } from './FormulaireJourAdmissible';
import { TableJoursAdmissibles } from './TableJoursAdmissibles';
import { ClassementPriorite } from './ClassementPriorite';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token')}` } });

// 🧾 Phase 3 : onglet « Jours admissibles » — sélecteur, formulaire, historique, filtre année, compteur, classement de priorité.
export function JoursAdmissibles({ installateurs }) {
  const [installateurId, setInstallateurId] = useState('');
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [jours, setJours] = useState([]);
  const [classement, setClassement] = useState([]);
  const [enEdition, setEnEdition] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const noms = Object.fromEntries(installateurs.map((i) => [i.id, i.nom]));

  const charger = useCallback(async () => {
    const q = `?annee=${annee}${installateurId ? `&installateur_id=${installateurId}` : ''}`;
    const [j, c] = await Promise.all([
      axios.get(`${API}/api/installateurs/jours-admissibles${q}`, auth()),
      axios.get(`${API}/api/installateurs/priorite?annee=${annee}`, auth()),
    ]);
    setJours(j.data.jours || []);
    setClassement(c.data.classement || []);
  }, [annee, installateurId]);

  useEffect(() => { charger().catch(() => toast.error('Impossible de charger les jours admissibles.')); }, [charger]);

  const action = async (fn, succes) => {
    setEnCours(true);
    try { await fn(); toast.success(succes); await charger(); return true; }
    catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || 'Erreur'); return false; }
    finally { setEnCours(false); }
  };
  const actifs = jours.filter((j) => j.statut === 'actif').length;

  return (
    <div className="space-y-4" data-testid="onglet-jours-contenu">
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm font-medium">Installateur</label>
        <select className="border rounded-md h-9 px-2 text-sm bg-white" value={installateurId} data-testid="jours-selecteur-installateur" onChange={(e) => setInstallateurId(e.target.value)}>
          <option value="">Tous les installateurs</option>
          {installateurs.map((i) => <option key={i.id} value={i.id}>{i.nom}</option>)}
        </select>
        <label className="text-sm font-medium">Année</label>
        <input type="number" className="border rounded-md h-9 px-2 text-sm w-24" value={annee} data-testid="jours-filtre-annee" onChange={(e) => setAnnee(parseInt(e.target.value) || annee)} />
        <span className="text-sm font-semibold text-amber-800" data-testid="jours-compteur">{actifs} jour{actifs > 1 ? 's' : ''} admissible{actifs > 1 ? 's' : ''} en {annee}</span>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">{enEdition ? 'Modifier le jour admissible' : 'Ajouter un jour de fin de semaine admissible'}</CardTitle></CardHeader>
        <CardContent>
          <FormulaireJourAdmissible key={enEdition?.id || installateurId} installateurs={installateurs} installateurId={installateurId} enCours={enCours}
            initial={enEdition} onAnnuler={() => setEnEdition(null)}
            onSoumettre={(corps) => (enEdition
              ? action(() => axios.put(`${API}/api/installateurs/jours-admissibles/${enEdition.id}`, corps, auth()), 'Jour modifié').then((ok) => { if (ok) setEnEdition(null); return ok; })
              : action(() => axios.post(`${API}/api/installateurs/jours-admissibles`, corps, auth()), 'Jour admissible ajouté'))} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Historique ({jours.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <TableJoursAdmissibles jours={jours} nomsInstallateurs={noms} onModifier={setEnEdition}
            onSupprimer={(j) => window.confirm(`Supprimer le jour admissible du ${j.date} ? (conservé dans l'historique)`) && action(() => axios.delete(`${API}/api/installateurs/jours-admissibles/${j.id}`, auth()), 'Jour supprimé')} />
        </CardContent>
      </Card>

      <ClassementPriorite classement={classement} annee={annee} />
    </div>
  );
}
